import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NbJSThemeOptions, NbThemeService } from '@nebular/theme';
import { baseTheme } from '@shared/styles/base.theme';
import { ChartConfiguration, ChartData, ChartType, Color } from 'chart.js';
import { BaseChartDirective, ThemeService } from 'ng2-charts';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-doughnut',
  templateUrl: './doughnut.component.html',
  styleUrls: ['./doughnut.component.scss'],
  providers: [ThemeService]
})
export class DoughnutComponent implements OnInit {
  @ViewChild(BaseChartDirective)  public chart!: BaseChartDirective;

  @Input() descriptionText: string = '';
  @Input() chartData!: Array<{ name: string, value: number }>;
  @Input() height: number = 110;
  @Input() width: number = 110;
  @Input() showTooltips = false;
  @Input() colors?: Array<string>;
  @Input() tooltipsLabel: Array<string> = [];

  doughnutChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  doughnutChartType: ChartType = 'doughnut';
  doughnutChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        filter: (tooltip) => {
          return this.doughnutChartData.labels?.length ? (this.doughnutChartData.labels[0] as Array<string>)[tooltip.dataIndex] !== '' : false;
        },
        callbacks: {
          title: (items: any) => {
            return items && items[0] && this.doughnutChartData.labels ? this.doughnutChartData.labels[items[0].dataIndex] as string : '';
          },
          label: (item: any) => {
            return this.tooltipsLabel[item.dataIndex];
          }
        }
      },
    },
    elements: {
      arc: {
        borderWidth: 0
      },
    }
  }

  private subscription = new Subscription();
  private nbTheme: NbJSThemeOptions;

  constructor(
    private themeService: ThemeService,
    private nbThemeService: NbThemeService
  ) { }

  ngOnInit() {
    this.subscription.add(
      this.nbThemeService.getJsTheme()
        .subscribe(theme => {
          this.nbTheme = theme;
          this.chart && this.updateDoughnut(this.chartData, this.descriptionText);
        })
    );

    this.updateDoughnut(this.chartData, this.descriptionText);

    if (this.doughnutChartOptions?.plugins?.tooltip?.enabled) {
      this.doughnutChartOptions.plugins.tooltip.enabled = this.showTooltips;
    }

    this.themeService.setColorschemesOptions(this.doughnutChartOptions);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public toImage(): string {
    return this.chart?.toBase64Image() || '';
  }

  updateDoughnut(chartData: Array<{ name: string, value: number }>, descriptionText: string): void {
    this.chartData = chartData;

    const data = this.chartData.map(({ value }) => value);
    const valuesEmpty = !data.length || !Math.max(...data);
    const colors = this.createColors(valuesEmpty);

    valuesEmpty && data.push(0, 1);

    this.doughnutChartData.datasets = [{
      data,
      backgroundColor: colors,
      hoverBackgroundColor: colors
    }];

    this.doughnutChartData.labels = this.chartData.map(({ name }) => name);
    this.descriptionText = descriptionText;

    this.themeService.setColorschemesOptions(this.doughnutChartOptions);
    this.chart?.update(); 
  }

  createColors(skipDefined: boolean = false): Color[] {
    if (!skipDefined && this.colors) {
      return this.colors;
    }

    const variables = this.nbTheme ? this.nbTheme.variables! : baseTheme;
    const primaryColor = this.chartData[1]?.value <= 0 ? variables['doughnutChartDanger'] : variables['doughnutChartPrimary'];
    const secondaryColor = variables['doughnutChartBackground'];
    
    return [ primaryColor as string, secondaryColor as string ];
  }
}
